/* LEGAL-NOTICE
   © Copyright 2018 - Quid Informatica S.p.A. - All Rights Reserved
   https://quidinfo.it/qinetic/licence.html - "Quid Consumer Lending Platform"
   Deposito SIAE n. 012702 del 26 Luglio 2018
LEGAL-NOTICE */

/**
 * trova il primo valore boolean tra gli argomenti
 */
export const firstBoolean = (...values: any[]): boolean => {
  const booleanVal = values && values.find(x => typeof x === 'boolean');

  if (typeof booleanVal === 'boolean') {
    return booleanVal;
  } else {
    return false;
  }
};

/**
 * trova il primo numero maggiore o uguale a 0
 */
export const firstPositiveNumber = (...values: any[]): number => {
  const positiveNumber =
    values && values.find(x => typeof x === 'number' && x >= 0);

  if (typeof positiveNumber === 'number') {
    return positiveNumber;
  } else {
    return -1;
  }
};

/**
 * restituisce il numero start se entro i limiti, altrimenti restituisce il min
 */
export const startOrMinNumber = (
  start: number,
  limits: { min: number; max: number }
): number => {
  if (typeof start === 'number' && start < limits.max && start > limits.min) {
    return start;
  } else {
    return limits.min;
  }
};

/**
 * restituisce numero intero casuale dentro il range definito
 * (non utilizzare per generare numeri rilevanti. es password, codici, id, ecc)
 */
export const randNumInRange = (min: number, max: number) =>
  // tslint:disable-next-line: comment-format
  Math.round(Math.random() * (max - min) + min); //NOSONAR;

/**
 * restituisce percentuale casuale
 */
export const randPerc = () =>
  // tslint:disable-next-line: comment-format
  Math.floor(Math.random() * 100) + 1; //NOSONAR;

/**
 * restituisce numero arrotondato con 2 decimali
 */
export const twoDecimals = (num: number): number => Math.round(num * 100) / 100;

/**
 * sostituisce i "-" con i valore di rimpiazzo ("_")
 */
export const replaceDash = (str: string, replaceValue: string = '_'): string =>
  replaceAll(str, '-', replaceValue);

/**
 * sostituisce i "_" con i valore di rimpiazzo ("-")
 */
export const replaceUnderscore = (
  str: string,
  replaceValue: string = '-'
): string => replaceAll(str, '_', replaceValue);

/**
 * sostituisce tutte le occorrenze del find nella stringa
 */
export const replaceAll = (
  str: string,
  find: string,
  replace: string
): string => str.replace(new RegExp(find, 'g'), replace);

/**
 * restituisce la data sommando gli anni richiesti rispetto ad oggi
 */
export const dateAfterYears = (years: number): Date => {
  const dt = new Date();

  dt.setFullYear(dt.getFullYear() + years);

  return dt;
};

/**
 * restituisce la data sottraendo gli anni richiesti rispetto ad oggi
 */
export const dateBeforeYears = (years: number): Date => {
  const dt = new Date();

  dt.setFullYear(dt.getFullYear() - years);

  return dt;
};

/**
 * converte la data in formato backend (number di 8 cifre)
 * in un formato in stringa per il frontend
 */
export const beToFeDate = (input: any): string => {
  if (typeof input === 'number' && input.toString().length === 8) {
    const str = input.toString();
    const yyyy = str.substring(0, 4);
    const mm = str.substring(4, 6);
    const dd = str.substring(6, 8);

    return `${yyyy}-${mm}-${dd}`;
  } else if (typeof input === 'string') {
    return input;
  } else {
    return null;
  }
};

/**
 * converte la stringa che rappresenta una data
 * dal formato europero (dd/mm/yyyy) al formato americano (mm/dd/yyyy)
 */
export const euToUsDate = (input: string, separator?: '/' | '-'): string => {
  if (typeof input !== 'string') {
    return null;
  }

  let parts: string[] = [];

  if (!!separator) {
    parts = input.split(separator);
  } else {
    parts = [
      input.substring(0, 2),
      input.substring(2, 4),
      input.substring(4, 8)
    ];
  }

  if (parts.length !== 3) {
    return input;
  }

  const mm = parts[1];
  const dd = parts[0];
  const yyyy = completeYearDecade(parts[2]);

  return `${mm}/${dd}/${yyyy}`;
};

/**
 * completa la decade restituendo l'anno completo in base al
 * decennio in cui ci si trova
 */
export const completeYearDecade = (decade: string): string => {
  if (typeof decade !== 'string' || isNaN(Number(decade))) {
    return null;
  }

  if (decade.length !== 2) {
    return decade;
  }

  const currentDecade = new Date().getFullYear().toString().substring(2);

  if (Number(decade) > Number(currentDecade)) {
    return '19' + decade;
  } else {
    return '20' + decade;
  }
};

/**
 * restituisce il mese in formato 2 digits + zero padded
 */
export const paddedMonth = (month: number): string => pad2(month);

/**
 * restituisce il giorno in formato 2 digits + zero padded
 */
export const paddedDay = (day: number): string => pad2(day);

/**
 * restituisce il giorno in formato 2 digits + zero padded
 */
export const pad2 = (num: number): string => num.toString().padStart(2, '0');

/**
 * controlla che l'input sia un url valido
 */
export const isValidUrl = (input: string): boolean => {
  let url;

  try {
    url = new URL(input);
  } catch (_) {
    return false;
  }

  return (
    url.protocol === 'http:' ||
    url.protocol === 'https:' ||
    url.protocol === 'tel:'
  );
};

/**
 * restituisce il testo intero o tagliato in base alla sua lunghezza
 */
export const truncateText = (
  text: string,
  options: { maxLength: number } = { maxLength: 100 }
): string => {
  const ellipsis = '...';

  if (typeof text !== 'string') {
    return '';
  } else if (text.length <= options.maxLength) {
    return text;
  } else {
    return text.slice(0, options.maxLength - ellipsis.length) + ellipsis;
  }
};

/**
 * ordina in modo casuale l'array (https://dev.to/codebubb/how-to-shuffle-an-array-in-javascript-2ikj)
 */
export const shuffleArray = <T>(array: T[]): T[] => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const temp = array[i];
    array[i] = array[j];
    array[j] = temp;
  }

  return array;
};

/**
 * esegue promise con ritardo
 */
export const sleep = (ms: number): Promise<any> =>
  new Promise(resolve => setTimeout(resolve, ms));

/**
 * se il valore è un numero restituisce il numero, altrimenti restituisce null
 */
export const numOrZero = (value: any): number =>
  isNaN(value) || value === null ? 0 : Number(value);

/**
 * esegue funzione instant() immediatamente e funzione deferred() con timeout
 */
export const differ = (
  instant: () => void,
  deferred: () => void,
  options: { delay?: number } = {}
): void => {
  instant();

  setTimeout(
    () => {
      deferred();
    },
    typeof options.delay === 'number' ? options.delay : 0
  );
};
